
import React, { useEffect } from 'react';
import { Tabs } from '@/components/ui/tabs';
import { NavBar } from '@/components/navbar';
import { useIsMobile } from '@/hooks/use-mobile';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

// Import components
import BusinessSettingsHeader from '@/components/business/settings/BusinessSettingsHeader';
import BusinessTabsHeader from '@/components/business/settings/BusinessTabsHeader';
import BusinessTabContent from '@/components/business/settings/BusinessTabContent';
import { useBusinessSettingsState } from '@/hooks/useBusinessSettingsState';
import { Business } from '@/data/types/businessTypes';

const BusinessSettings = () => {
  const isMobile = useIsMobile();
  const { businessId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'users';
  
  const {
    business,
    activeTab,
    setActiveTab,
    formatCurrency,
    billingData,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    totalCharge,
    handleChangeBillingManager,
    handleAddPaymentMethod,
    handleAddPromoCode,
    handleMakePrimary,
    allBusinesses
  } = useBusinessSettingsState(initialTab);

  // Set the active tab based on URL search param
  useEffect(() => {
    if (searchParams.get('tab')) {
      setActiveTab(searchParams.get('tab') || 'billing');
    }
  }, [searchParams, setActiveTab]);

  const handleBackLocal = () => {
    navigate(`/business/${businessId}`);
  };

  // Handle business change in the dropdown
  const handleBusinessChange = (selectedBusinessId: string) => {
    // If "home" is selected, navigate to the home page
    if (selectedBusinessId === 'home') {
      navigate('/');
      return;
    }
    
    // Navigate to the settings page of the selected business with the current active tab
    navigate(`/business/${selectedBusinessId}/settings?tab=${activeTab}`);
  };

  // Find the actual business name from allBusinesses or use the one from business state
  const currentBusinessName = business?.name || 'Business';
  
  // Create a simplified billingData prop with all required fields
  const simplifiedBillingData = {
    activeBusinesses: billingData.activeBusinesses || [],
    pausedBusinesses: billingData.pausedBusinesses || [],
    managedBudgets: billingData.managedBudgets || [],
    availableCredits: billingData.availableCredits || 0,
    promotionalDiscount: billingData.promotionalDiscount || 0,
    totalCharge: totalCharge || 0,
    paymentMethods: billingData.paymentMethods || [],
    nextChargeDate: billingData.nextChargeDate || new Date(),
    invoices: billingData.invoices || [],
    billingManager: billingData.billingManager,
    promoCodes: billingData.promoCodes || [],
    discountCodes: billingData.discountCodes || [],
    billingHistory: billingData.billingHistory || [],
    selectedPaymentMethod: selectedPaymentMethod || '',
    setSelectedPaymentMethod: setSelectedPaymentMethod,
    handleAddPaymentMethod: handleAddPaymentMethod,
    handleAddPromoCode: handleAddPromoCode,
    handleMakePrimary: handleMakePrimary,
    handleChangeBillingManager: handleChangeBillingManager,
    formatCurrency
  };

  return (
    <div className="min-h-screen bg-background flex flex-col w-full overflow-x-hidden">
      <NavBar 
        businessName={currentBusinessName}
        allBusinesses={allBusinesses}
        onBusinessSelect={handleBusinessChange}
      />
      
      <BusinessSettingsHeader 
        handleBack={handleBackLocal} 
        businessName={currentBusinessName}
        businessId={businessId || ''}
      />

      <main className="flex-1 max-w-screen-xl mx-auto px-4 md:px-8 py-6 w-full overflow-x-hidden">
        <div className="mb-8 w-full overflow-x-hidden">
          <Tabs 
            defaultValue="users" 
            className="w-full" 
            value={activeTab} 
            onValueChange={(value) => setActiveTab(value)}
          >
            <BusinessTabsHeader
              activeTab={activeTab}
              isMobile={isMobile}
            />

            <BusinessTabContent 
              activeTab={activeTab}
              billingData={simplifiedBillingData}
            />
          </Tabs>
        </div>
      </main>
    </div>
  );
};

export default BusinessSettings;
